import React, { useState } from 'react';
import { Box, Typography, IconButton, Popover } from '@mui/material';
import { KeyboardArrowDown, ChevronLeft, ChevronRight, CalendarMonth, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { GANTT_VIEW_TYPES } from '../../../shared/appConstants';
import { DATE_DISPLAY_LABELS } from '../../../shared/appConstants';
import { DATE_PICKER_SELECT_YEAR } from '../../../shared/appConstants';
import GanttSettings from './GanttSettings';
import './GanttView.scss';

const GanttHeader = ({
  displayText,
  viewType,
  onViewTypeChange,
  onDatePickerOpen,
  onNavigate,
  configOptions,
  onConfigChange,
  groupByOption,
  onGroupByChange,
  tasks = [],
  dateRange,
  onYearSelect
}) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState('Week');
  const [yearAnchorEl, setYearAnchorEl] = useState(null);
  const [yearNavigation, setYearNavigation] = useState(0);

  const handleTabChange = (tab, type) => {
    setSelectedTab(tab);
    onViewTypeChange(type);
  };

  React.useEffect(() => {
    if (viewType === GANTT_VIEW_TYPES.DAY) setSelectedTab('Day');
    else if (viewType === GANTT_VIEW_TYPES.WEEK) setSelectedTab('Week');
    else if (viewType === GANTT_VIEW_TYPES.QUARTER) setSelectedTab('Quarter');
    else if (viewType === GANTT_VIEW_TYPES.YEAR) setSelectedTab('Year');
    if (viewType !== GANTT_VIEW_TYPES.YEAR) setYearAnchorEl(null);
  }, [viewType]);

  const handleDisplayClick = (event) => {
    if (viewType === GANTT_VIEW_TYPES.YEAR) {
      setYearAnchorEl(event.currentTarget);
      return;
    }
    onDatePickerOpen?.(event.currentTarget);
  };

  const yearOpen = Boolean(yearAnchorEl);
  const currentYear = dateRange?.fromDate ? new Date(dateRange.fromDate).getFullYear() : new Date().getFullYear();
  const yearDisplay = `${DATE_DISPLAY_LABELS.YEAR} (${currentYear})`;
  const displayLabel = viewType === GANTT_VIEW_TYPES.WEEK
    ? `${DATE_DISPLAY_LABELS.WEEK} (${displayText})`
    : displayText;
  const showCalendarIcon =
    viewType === GANTT_VIEW_TYPES.YEAR ||
    viewType === GANTT_VIEW_TYPES.WEEK ||
    viewType === GANTT_VIEW_TYPES.QUARTER ||
    viewType === GANTT_VIEW_TYPES.DAY;
  const baseYear = currentYear + yearNavigation;
  const years = [];
  for (let i = baseYear - 5; i <= baseYear + 4; i++) {
    years.push(i);
  }
  
  const handleYearNavigation = (direction) => {
    setYearNavigation(prev => prev + (direction * 10));
  };

  return (
    <Box className="gantt-header">
      <Box className="gantt-header-inner">
        <Box className="gantt-header-left">
          <Typography 
            className="gantt-display-text"
            onClick={handleDisplayClick}
          >
            <span className="gantt-display-text-content">
              <span className="gantt-display-text-label">
                {viewType === GANTT_VIEW_TYPES.YEAR ? yearDisplay : displayLabel}
              </span>
              {showCalendarIcon ? (
                <CalendarMonth className="gantt-calendar-icon" />
              ) : (
                <KeyboardArrowDown className="gantt-dropdown-icon" />
              )}
            </span>
          </Typography>
          <Box className="gantt-nav-buttons">
            <IconButton onClick={() => onNavigate(-1)} size="small">
              <ChevronLeft />
            </IconButton>
            <IconButton onClick={() => onNavigate(1)} size="small">
              <ChevronRight />
            </IconButton>
          </Box>
        </Box>


        <Popover
          open={yearOpen}
          anchorEl={yearAnchorEl}
          onClose={() => {
            setYearAnchorEl(null);
            setYearNavigation(0);
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ className: 'gantt-year-popover' }}
        >
          <Box className="gantt-year-popover-header">
            <IconButton size="small" onClick={() => handleYearNavigation(-1)}>
              <ChevronLeft fontSize="small" />
            </IconButton>
            <Typography className="gantt-year-popover-title">{DATE_PICKER_SELECT_YEAR}</Typography>
            <IconButton size="small" onClick={() => handleYearNavigation(1)}>
              <ChevronRight fontSize="small" />
            </IconButton>
            <IconButton className="gantt-year-popover-close" size="small" onClick={() => {
              setYearAnchorEl(null);
              setYearNavigation(0);
            }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Box className="gantt-year-popover-divider" />
          <Box className="gantt-year-grid">
            {years.map((year) => (
              <Box
                key={year}
                className={`gantt-year-card${currentYear === year ? " is-selected" : ""}`}
                onClick={() => {
                  onYearSelect?.(year);
                  setYearAnchorEl(null);
                  setYearNavigation(0);
                }}
              >
                <Typography className="gantt-year-card-title">{year}</Typography>
              </Box>
            ))}
          </Box>
        </Popover>
        <Box className="gantt-header-right">
          <Box className="gantt-view-tabs">
            <Box 
              className={`gantt-view-tab ${selectedTab === 'Day' ? 'gantt-view-tab-active' : ''}`}
              onClick={() => handleTabChange('Day', GANTT_VIEW_TYPES.DAY)}
            >
              {t('gantt.day')}
            </Box>
            <Box 
              className={`gantt-view-tab ${selectedTab === 'Week' ? 'gantt-view-tab-active' : ''}`}
              onClick={() => handleTabChange('Week', GANTT_VIEW_TYPES.WEEK)}
            >
              {t('gantt.week')}
            </Box>
            <Box 
              className={`gantt-view-tab ${selectedTab === 'Quarter' ? 'gantt-view-tab-active' : ''}`}
              onClick={() => handleTabChange('Quarter', GANTT_VIEW_TYPES.QUARTER)}
            >
              {t('gantt.quarter')}
            </Box>
            <Box 
              className={`gantt-view-tab ${selectedTab === 'Year' ? 'gantt-view-tab-active' : ''}`}
              onClick={() => handleTabChange('Year', GANTT_VIEW_TYPES.YEAR)}
            >
              {t('gantt.year')}
            </Box>
          </Box>
          <GanttSettings 
            configOptions={configOptions}
            onConfigChange={onConfigChange}
            groupByOption={groupByOption}
            onGroupByChange={onGroupByChange}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default GanttHeader;
